import { useState, useEffect } from 'react'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import styled from '@emotion/styled'
import ServiceMenusCard from 'components/pages/Index/ServiceMenusCard'
import NowrapDiv from 'components/common/NowrapDiv'
import { sortBy } from 'lodash-es'

const DEFAULT_SERVICELOGO = '/img/default_service_icon.svg'

const StyledTabs = styled(Tabs)(({ theme, isDownMd, isDownLg, isMobile }) => ({
  position: 'relative',
  paddingTop: isDownMd ? 16 : 24,
  paddingBottom: isDownMd ? 16 : 24,

  '& .MuiTab-root': {
    textTransform: 'none',
    padding: isDownMd ? '2px 0px 8px 0px' : '2px 4px 8px 4px',
    marginRight: isDownMd ? '2vw' : isDownLg ? '12px' : '24px',
    justifyContent: 'flex-start',
    width: isDownMd ? '15vw' : 'auto',
    minHeight: 0,
    minWidth: 0,
    '&:last-child': {
      marginRight: 0,
    },
  },

  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    height: 4,
    '&::after': {
      content: '""',
      display: 'block',
      width: 32,
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.palette.primary.main,
    },
  },

  '& .MuiTabs-scrollButtons': {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 10,
    width: 40,
    '&:first-of-type': {
      left: 0,
      background:
        'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 25%, rgba(255,255,255,0.5) 70%, rgba(255,255,255,0) 100%)',
    },
    '&:last-of-type': {
      right: 0,
      background:
        'linear-gradient(270deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 25%, rgba(255,255,255,0.5) 70%, rgba(255,255,255,0) 100%)',
    },
  },

  '& .MuiTabs-scroller': {
    overflow: isMobile ? 'auto' : 'hidden',
    position: 'relative',
  },
}))

// https://angel-rs.github.io/css-color-filter-generator/ hex 轉 filter 網站
// isSelected ? '#75AA00' : '#5A5A5A'
const TabIcon = styled.img(({ isSelected }) => ({
  width: 24,
  height: 24,
  filter: isSelected
    ? 'brightness(0) saturate(100%) invert(62%) sepia(38%) saturate(3133%) hue-rotate(40deg) brightness(90%) contrast(101%)'
    : 'brightness(0) saturate(100%) invert(35%) sepia(2%) saturate(3%) hue-rotate(22deg) brightness(92%) contrast(85%)',
}))

const StyledTab = styled(Tab)(({ theme, isSelected }) => ({
  ...(isSelected ? theme.typography.labelMedium : theme.typography.bodyXs),
}))

function TabPanel({ value, index, subcatalogs, tab, onSubMenusLinkClick }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`icon-tabpanel-${index}`}>
      {value === index && (
        <ServiceMenusCard
          subcatalogs={subcatalogs}
          tab={tab}
          onSubMenusLinkClick={onSubMenusLinkClick}
        />
      )}
    </div>
  )
}

export default function ServiceMenusTabs({
  catalogsServiceMenus,
  onTabChange,
  onSubMenusLinkClick,
  isMobile,
}) {
  const [value, setValue] = useState(0)
  const theme = useTheme()
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'))
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const tabData = sortBy(
    catalogsServiceMenus?.flatMap(
      (catalog) =>
        catalog.subMenus?.map((subMenu) => ({
          label: subMenu.name,
          linkDisplayName: subMenu.linkDisplayName,
          imageUrl: subMenu.defaultCategoryImage?.url,
          subcatalogs: subMenu.subcatalogs || [],
          link: subMenu.link || '',
          linkTarget: subMenu.linkTarget || 'self',
          sort: subMenu.sort || 0,
          createdAt: subMenu.createdAt,
          _id: subMenu?._id,
        })) || []
    ) || [],
    [
      (item) => -(item.sort || 0),
      // createdAt 排序（新到舊）
      // 如果 createdAt 不存在，則視為最舊
      (item) => (item.createdAt ? -new Date(item.createdAt).getTime() : 0),
    ]
  )

  const handleChange = (event, newValue) => {
    setValue(newValue)
    onTabChange?.(tabData[newValue], true)
  }

  useEffect(() => {
    if (tabData.length > 0 && onTabChange) {
      onTabChange(tabData[0])
    }
  }, [])

  const createTabIcon = (imageUrl, isSelected) => (
    <TabIcon src={imageUrl || DEFAULT_SERVICELOGO} isSelected={isSelected} />
  )

  return (
    <Box mb={4}>
      <StyledTabs
        value={value}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons={isMobile ? false : 'auto'}
        allowScrollButtonsMobile
        isDownMd={isDownMd}
        isDownLg={isDownLg}
        isMobile={isMobile}
      >
        {tabData.map((tab, idx) => (
          <StyledTab
            key={`${tab.label}-${idx}`}
            icon={createTabIcon(tab.imageUrl, value === idx)}
            label={
              <NowrapDiv $lines={2} $textCenter={true}>
                {tab.label}
              </NowrapDiv>
            }
            isSelected={value === idx}
          />
        ))}
      </StyledTabs>
      {tabData.map((tab, idx) => (
        <TabPanel
          key={`${tab.label}-${idx}`}
          value={value}
          index={idx}
          subcatalogs={tab.subcatalogs}
          tab={tab}
          onSubMenusLinkClick={onSubMenusLinkClick}
        />
      ))}
    </Box>
  )
}
